<template>
  <v-container fluid class="public-settings pa-4">
    <form @submit.prevent="save">
      <div class="settings-heading">
        <div>
          <h1 class="headline">Public website settings</h1>
          <p class="caption grey--text mb-0">Manage the appearance and shared credits of the artwork catalogue.</p>
        </div>
        <div class="settings-actions">
          <span v-if="changed" class="caption grey--text">Unsaved changes</span>
          <v-btn type="submit" color="black" dark :loading="saving" :disabled="loading || saving || !!loadError || !changed">Save settings</v-btn>
        </div>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="black"></v-progress-linear>
      <v-alert v-else-if="loadError" :value="true" type="error" outline>
        {{ loadError }}
        <v-btn flat small @click="loadSettings">Retry</v-btn>
      </v-alert>

      <template v-else>
        <section class="settings-section" aria-labelledby="display-heading">
          <div class="section-introduction">
            <h2 id="display-heading" class="title">Catalogue display</h2>
            <p class="body-1 grey--text">Choose how artwork images appear in the collection grid.</p>
          </div>
          <div class="section-controls">
            <div class="mode-grid" role="group" aria-label="Catalogue grid layout">
              <button v-for="mode in modes" :key="mode.value" type="button"
                class="mode-card" :class="{ 'mode-card--selected': settings.grid_display_mode === mode.value }"
                :aria-pressed="settings.grid_display_mode === mode.value" :disabled="saving" @click="selectMode(mode.value)">
                <span class="mode-preview" :class="`mode-preview--${mode.value}`" aria-hidden="true">
                  <span v-for="n in 6" :key="n"></span>
                </span>
                <span class="mode-title">{{ mode.title }}<span v-if="settings.grid_display_mode === mode.value" aria-hidden="true">✓</span></span>
                <small>{{ mode.description }}</small>
              </button>
            </div>
            <div class="reflection-setting">
              <div>
                <div class="subheading">Mirror image behind text</div>
                <p class="caption grey--text mb-0">Add a subtle mirrored continuation behind each artwork caption.</p>
              </div>
              <v-switch v-model="settings.grid_reflection_enabled" color="black" hide-details :disabled="saving" aria-label="Mirror image behind text"></v-switch>
            </div>
          </div>
        </section>

        <section class="settings-section" aria-labelledby="copyright-heading">
          <div class="section-introduction">
            <h2 id="copyright-heading" class="title">Copyright credits</h2>
            <p class="body-1 grey--text">Shared annual credit for artists represented by image societies.</p>
            <p class="caption grey--text">Enable “Annual copyright credit” in an artist's Public catalogue settings to show this text on their artworks.</p>
          </div>
          <div class="section-controls">
            <div class="credit-instructions">
              <p class="body-1 mb-1">The year updates automatically.</p>
              <p class="caption grey--text mb-0">Use <code>{year}</code> for the current year and <code>{artist}</code> for the artist's name. Leave a language blank to hide its annual credit.</p>
            </div>
            <div class="credit-fields">
              <div v-for="locale in locales" :key="locale.value" class="credit-field">
                <v-textarea v-model="settings['annual_copyright_credit_' + locale.value]"
                  :label="locale.label + ' copyright credit'" :disabled="saving" color="black"
                  box auto-grow rows="3" placeholder="© {artist}, {year}"></v-textarea>
                <div class="credit-preview">
                  <span class="preview-label">{{ locale.label }} preview · {{ currentYear }}</span>
                  <p v-if="creditPreview(locale.value)" class="body-1 mb-0">{{ creditPreview(locale.value) }}</p>
                  <p v-else class="caption grey--text mb-0">No annual credit will appear in this language.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>
    </form>
    <v-snackbar v-model="snackbar" right top :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <v-btn flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from '@/utils/apiClient'
import { normalizePublicSettings, previewAnnualCopyright } from '@/utils/publicWebsiteSettings'

export default {
  name: 'PublicSettings',
  data () {
    return {
      settings: normalizePublicSettings(),
      savedSettings: normalizePublicSettings(),
      modes: [
        { value: 'artistic', title: 'Artistic', description: 'Equal frames with complete images and blurred backgrounds.' },
        { value: 'masonry', title: 'Dynamic heights', description: 'Fixed columns with natural image heights.' },
        { value: 'bottom_aligned', title: 'Bottom aligned', description: 'Image bottoms aligned across each row.' }
      ],
      locales: [{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }],
      currentYear: new Date().getFullYear(),
      loading: true, loadError: '', saving: false,
      snackbar: false, snackbarColor: 'black', snackbarText: ''
    }
  },
  computed: {
    changed () {
      return JSON.stringify(this.settings) !== JSON.stringify(this.savedSettings)
    }
  },
  created () { this.loadSettings() },
  methods: {
    loadSettings () {
      this.loading = true
      this.loadError = ''
      return axios.get(`${this.$globals.apiserver}/public_setting.json`)
        .then(response => {
          this.settings = normalizePublicSettings(response.data)
          this.savedSettings = normalizePublicSettings(response.data)
        })
        .catch(() => { this.loadError = 'Could not load public website settings.' })
        .then(() => { this.loading = false })
    },
    selectMode (mode) { this.settings.grid_display_mode = mode },
    creditPreview (locale) {
      return previewAnnualCopyright(this.settings['annual_copyright_credit_' + locale], this.currentYear)
    },
    save () {
      if (this.loading || this.saving || this.loadError || !this.changed) return
      this.saving = true
      return axios.patch(`${this.$globals.apiserver}/public_setting.json`, { public_setting: normalizePublicSettings(this.settings) })
        .then(response => {
          this.settings = normalizePublicSettings(response.data)
          this.savedSettings = normalizePublicSettings(response.data)
          this.notify('Public website settings saved.', 'black')
        })
        .catch(error => {
          const errors = error.response && error.response.data && error.response.data.errors
          this.notify(errors ? errors.join(', ') : 'Could not save public website settings.', 'red')
        })
        .then(() => { this.saving = false })
    },
    notify (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.public-settings { max-width: 1400px; }
.settings-heading { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 20px 0 24px; border-top: 1px solid #000; }
.settings-heading h1 { margin: 0 0 6px; }
.settings-actions { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.settings-actions .v-btn { margin-right: 0; }
.settings-actions .v-btn.v-btn--disabled { color: #777 !important; background-color: #eee !important; }
.settings-section { display: grid; grid-template-columns: 230px minmax(0, 1fr); gap: 44px; padding: 32px 0; border-top: 1px solid #ddd; }
.section-introduction h2 { margin-bottom: 12px; }
.section-introduction p { line-height: 1.6; }
.section-controls { min-width: 0; }
.mode-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.mode-card { min-width: 0; padding: 16px; border: 1px solid #ccc; background: #fff; text-align: left; transition: border-color .15s; }
.mode-card:hover { border-color: #000; }
.mode-card:focus-visible { outline: 2px solid #000; outline-offset: 3px; }
.mode-card--selected { border-color: #000; box-shadow: inset 0 0 0 1px #000; }
.mode-title { display: flex; justify-content: space-between; gap: 8px; font-size: 16px; font-weight: 600; margin-top: 18px; }
.mode-card small { display: block; margin-top: 6px; color: #777; line-height: 1.5; }
.mode-preview { display: flex; align-items: flex-end; gap: 4px; height: 90px; overflow: hidden; }
.mode-preview span { display: block; flex: 1; border: 1px solid #aaa; background: #e5e5e5; }
.mode-preview--artistic span { height: 54px; }
.mode-preview--artistic span:nth-child(2n) { background: #bcbcbc; }
.mode-preview--masonry { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-template-rows: repeat(2, 42px); align-items: start; gap: 4px; }
.mode-preview--masonry span:nth-child(3n+1) { height: 22px; }
.mode-preview--masonry span:nth-child(3n+2) { height: 32px; }
.mode-preview--masonry span:nth-child(3n) { height: 42px; }
.mode-preview--bottom_aligned span:nth-child(3n+1) { height: 34px; }
.mode-preview--bottom_aligned span:nth-child(3n+2) { height: 58px; }
.mode-preview--bottom_aligned span:nth-child(3n) { height: 80px; }
.reflection-setting { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 20px 0 0; margin-top: 20px; border-top: 1px solid #eee; }
.reflection-setting .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.credit-instructions { background: #f7f7f7; padding: 16px 20px; margin-bottom: 20px; }
.credit-instructions code { color: #333; box-shadow: none; background: #eaeaea; font-size: inherit; }
.credit-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.credit-preview { padding: 14px 16px; border-left: 2px solid #ddd; overflow-wrap: anywhere; }
.credit-preview p { white-space: pre-wrap; }
.preview-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #777; margin-bottom: 8px; }
@media (max-width: 959px) { .settings-section { grid-template-columns: 1fr; gap: 16px; } .section-introduction p { margin-bottom: 4px; } }
@media (max-width: 599px) { .settings-heading { align-items: flex-start; flex-direction: column; gap: 16px; } .settings-actions { align-self: flex-end; } .mode-grid, .credit-fields { grid-template-columns: 1fr; } .mode-card { display: grid; grid-template-columns: 100px 1fr; gap: 0 20px; } .mode-preview { grid-row: span 2; height: 70px; } .mode-title { margin-top: 6px; } .mode-preview--masonry { grid-template-rows: repeat(2, 32px); } .mode-preview--masonry span { max-height: 32px; } .mode-preview--bottom_aligned span { max-height: 68px; } .settings-section { padding: 24px 0; } }
</style>



// WEBPACK FOOTER //
// src/components/public/PublicSettings.vue